import { BookingPriceOverrideItem } from '../api/BookingPriceOverrideItem';
import { SignupPaymentType, StudentStatus, StudentTypes, WebsiteBlockContactFormType, WebsiteBlockEmbedContentType, WebsiteBlockEmbedType, WebsiteBlockLoginFormType, WebsiteBlockReviewsType, WebsiteBlockSignupFormType, WebsiteBlockTextAndMediaType, WebsiteBlockType } from '../api/Enums';
import { WebsiteBlockBlogDisplayItem } from '../api/WebsiteBlockBlogDisplayItem';
import { WebsiteBlockBlogInputItem } from '../api/WebsiteBlockBlogInputItem';
import { WebsiteBlockContactFormDisplayItem } from '../api/WebsiteBlockContactFormDisplayItem';
import { WebsiteBlockContactFormInputItem } from '../api/WebsiteBlockContactFormInputItem';
import { WebsiteBlockDisplayItem } from '../api/WebsiteBlockDisplayItem';
import { WebsiteBlockEmbedDisplayItem } from '../api/WebsiteBlockEmbedDisplayItem';
import { WebsiteBlockEmbedInputItem } from '../api/WebsiteBlockEmbedInputItem';
import { WebsiteBlockInputItem } from '../api/WebsiteBlockInputItem';
import { WebsiteBlockLoginFormDisplayItem } from '../api/WebsiteBlockLoginFormDisplayItem';
import { WebsiteBlockLoginFormInputItem } from '../api/WebsiteBlockLoginFormInputItem';
import { WebsiteBlockReviewsDisplayItem } from '../api/WebsiteBlockReviewsDisplayItem';
import { WebsiteBlockReviewsInputItem } from '../api/WebsiteBlockReviewsInputItem';
import { WebsiteBlockSignupFormAdditionalFieldsDisplayItem } from '../api/WebsiteBlockSignupFormAdditionalFieldsDisplayItem';
import { WebsiteBlockSignupFormAdditionalFieldsInputItem } from '../api/WebsiteBlockSignupFormAdditionalFieldsInputItem';
import { WebsiteBlockSignupFormDisplayItem } from '../api/WebsiteBlockSignupFormDisplayItem';
import { WebsiteBlockSignupFormInputItem } from '../api/WebsiteBlockSignupFormInputItem';
import { WebsiteBlockTextAndMediaActionDisplayItem } from '../api/WebsiteBlockTextAndMediaActionDisplayItem';
import { WebsiteBlockTextAndMediaDisplayItem } from '../api/WebsiteBlockTextAndMediaDisplayItem';
import { WebsiteBlockTextAndMediaImageDisplayItem } from '../api/WebsiteBlockTextAndMediaImageDisplayItem';
import { WebsiteBlockTextAndMediaInputItem } from '../api/WebsiteBlockTextAndMediaInputItem';
import { WebsiteCustomFieldContactFormDisplayItem } from '../api/WebsiteCustomFieldContactFormDisplayItem';
import { WebsiteCustomFieldSignupFormDisplayItem } from '../api/WebsiteCustomFieldSignupFormDisplayItem';
import { WebsiteRenderContextDisplayItem } from '../api/WebsiteRenderContextDisplayItem';
import { PublicReviewDisplayItem, ReviewSummaryDisplayItem } from '../public-api';
import { AssertExhausted, DesignBlockMode } from './base.definitions';

declare module '../api/WebsiteBlockDisplayItem' {
    namespace WebsiteBlockDisplayItem {
        let IsBlog: typeof isBlog;
        let IsContactForm: typeof isContactForm;
        let IsEmbed: typeof isEmbed;
        let IsReviews: typeof isReviews;
        let IsLoginForm: typeof isLoginForm;
        let IsSignupForm: typeof isSignupForm;
        let IsTextAndMedia: typeof isTextAndMedia;
        let HasImages: typeof hasImages;

        let AsBlog: typeof asBlog;
        let AsContactForm: typeof asContactForm;
        let AsEmbed: typeof asEmbed;
        let AsReviews: typeof asReviews;
        let AsLoginForm: typeof asLoginForm;
        let AsSignupForm: typeof asSignupForm;
        let AsTextAndMedia: typeof asTextAndMedia;
        let AsHasImages: typeof asHasImages;

        let ConvertToInputItem: typeof convertToInputItem;

        type WithImages = WebsiteBlockContactFormDisplayItem | WebsiteBlockLoginFormDisplayItem | WebsiteBlockTextAndMediaDisplayItem;
    }

    export interface WebsiteBlockDisplayItem {
        DesignBlockMode?: DesignBlockMode;
    }
}

WebsiteBlockDisplayItem.IsBlog = isBlog;
WebsiteBlockDisplayItem.IsContactForm = isContactForm;
WebsiteBlockDisplayItem.IsEmbed = isEmbed;
WebsiteBlockDisplayItem.IsReviews = isReviews;
WebsiteBlockDisplayItem.IsLoginForm = isLoginForm;
WebsiteBlockDisplayItem.IsSignupForm = isSignupForm;
WebsiteBlockDisplayItem.IsTextAndMedia = isTextAndMedia;
WebsiteBlockDisplayItem.HasImages = hasImages;

WebsiteBlockDisplayItem.AsBlog = asBlog;
WebsiteBlockDisplayItem.AsContactForm = asContactForm;
WebsiteBlockDisplayItem.AsEmbed = asEmbed;
WebsiteBlockDisplayItem.AsReviews = asReviews;
WebsiteBlockDisplayItem.AsLoginForm = asLoginForm;
WebsiteBlockDisplayItem.AsSignupForm = asSignupForm;
WebsiteBlockDisplayItem.AsTextAndMedia = asTextAndMedia;
WebsiteBlockDisplayItem.AsHasImages = asHasImages;

WebsiteBlockDisplayItem.ConvertToInputItem = convertToInputItem;

function isBlog(item: WebsiteBlockDisplayItem): item is WebsiteBlockBlogDisplayItem {
    return item.WebsiteBlockType === WebsiteBlockType.Blog;
}

function isContactForm(item: WebsiteBlockDisplayItem): item is WebsiteBlockContactFormDisplayItem {
    return item.WebsiteBlockType === WebsiteBlockType.ContactForm;
}

function isEmbed(item: WebsiteBlockDisplayItem): item is WebsiteBlockEmbedDisplayItem {
    return item.WebsiteBlockType === WebsiteBlockType.Embed;
}

function isReviews(item: WebsiteBlockDisplayItem): item is WebsiteBlockReviewsDisplayItem {
    return item.WebsiteBlockType === WebsiteBlockType.Reviews;
}

function isLoginForm(item: WebsiteBlockDisplayItem): item is WebsiteBlockLoginFormDisplayItem {
    return item.WebsiteBlockType === WebsiteBlockType.LoginForm;
}

function isSignupForm(item: WebsiteBlockDisplayItem): item is WebsiteBlockSignupFormDisplayItem {
    return item.WebsiteBlockType === WebsiteBlockType.SignupForm;
}

function isTextAndMedia(item: WebsiteBlockDisplayItem): item is WebsiteBlockTextAndMediaDisplayItem {
    return item.WebsiteBlockType === WebsiteBlockType.TextAndMedia;
}

function isGalleryBlock(item: WebsiteBlockDisplayItem): boolean {
    return WebsiteBlockDisplayItem.IsTextAndMedia(item)
            && [WebsiteBlockTextAndMediaType.PhotoGalleryCarousel, WebsiteBlockTextAndMediaType.PhotoGalleryCarouselOverlay, 
                WebsiteBlockTextAndMediaType.PhotoGalleryGrid, WebsiteBlockTextAndMediaType.GalleryCircleColumn, 
                WebsiteBlockTextAndMediaType.GallerySquareColumn, WebsiteBlockTextAndMediaType.GallerySquareAccentColumn,
                WebsiteBlockTextAndMediaType.GalleryCircleAccentColumn, WebsiteBlockTextAndMediaType.GalleryCircleCardColumn, 
                WebsiteBlockTextAndMediaType.GallerySquareCardColumn
            ].includes(item.TextAndMediaType);
}

function hasImages(item: WebsiteBlockDisplayItem): item is WebsiteBlockDisplayItem.WithImages {
    return WebsiteBlockDisplayItem.IsContactForm(item)
              && [WebsiteBlockContactFormType.MediaLeft, WebsiteBlockContactFormType.MediaRight].includes(item.ContactFormType)
           || WebsiteBlockDisplayItem.IsLoginForm(item)
              && [WebsiteBlockLoginFormType.MediaLeft, WebsiteBlockLoginFormType.MediaRight].includes(item.LoginFormType)
           || WebsiteBlockDisplayItem.IsTextAndMedia(item)
              && WebsiteBlockTextAndMediaDisplayItem.HasImages(item);
}

function asBlog(item: WebsiteBlockDisplayItem): WebsiteBlockBlogDisplayItem {
    if (isBlog(item)) {return item; }
    throw Error(`${WebsiteBlockDisplayItem.ClassName} is not a ${WebsiteBlockBlogDisplayItem.ClassName}.`);
}

function asContactForm(item: WebsiteBlockDisplayItem): WebsiteBlockContactFormDisplayItem {
    if (isContactForm(item)) { return item; }
    throw Error(`${WebsiteBlockDisplayItem.ClassName} is not a ${WebsiteBlockContactFormDisplayItem.ClassName}.`);
}

function asEmbed(item: WebsiteBlockDisplayItem): WebsiteBlockEmbedDisplayItem {
    if (isEmbed(item)) {return item; }
    throw Error(`${WebsiteBlockDisplayItem.ClassName} is not a ${WebsiteBlockEmbedDisplayItem.ClassName}.`);
}

function asReviews(item: WebsiteBlockDisplayItem): WebsiteBlockReviewsDisplayItem {
    if (isReviews(item)) {return item; }
    throw Error(`${WebsiteBlockDisplayItem.ClassName} is not a ${WebsiteBlockReviewsDisplayItem.ClassName}.`);
}

function asLoginForm(item: WebsiteBlockDisplayItem): WebsiteBlockLoginFormDisplayItem {
    if (isLoginForm(item)) { return item; }
    throw Error(`${WebsiteBlockDisplayItem.ClassName} is not a ${WebsiteBlockLoginFormDisplayItem.ClassName}.`);
}

function asSignupForm(item: WebsiteBlockDisplayItem): WebsiteBlockSignupFormDisplayItem {
    if (isSignupForm(item)) { return item; }
    throw Error(`${WebsiteBlockDisplayItem.ClassName} is not a ${WebsiteBlockSignupFormDisplayItem.ClassName}.`);
}

function asTextAndMedia(item: WebsiteBlockDisplayItem): WebsiteBlockTextAndMediaDisplayItem {
    if (isTextAndMedia(item)) { return item; }
    throw Error(`${WebsiteBlockDisplayItem.ClassName} is not a ${WebsiteBlockTextAndMediaDisplayItem.ClassName}.`);
}

function asHasImages(item: WebsiteBlockDisplayItem): WebsiteBlockDisplayItem.WithImages {
    if (hasImages(item)) {return item; }
    throw Error(`${WebsiteBlockDisplayItem.ClassName} does not have images.`);
}

function convertToInputItem(displayItem: WebsiteBlockDisplayItem): WebsiteBlockInputItem {
    switch (displayItem.WebsiteBlockType) {
        case WebsiteBlockType.TextAndMedia:
            const textAndMediaBlock = WebsiteBlockDisplayItem.AsTextAndMedia(displayItem);
            return new WebsiteBlockTextAndMediaInputItem({
                OrderIndex: textAndMediaBlock.OrderIndex,
                Header: textAndMediaBlock.Header,
                Body: isGalleryBlock(textAndMediaBlock) ? '' : textAndMediaBlock.Body,
                Images: textAndMediaBlock.Images.map(image => WebsiteBlockTextAndMediaImageDisplayItem.ConvertToInputItem(image)),
                TextAndMediaType: textAndMediaBlock.TextAndMediaType,
                Action: textAndMediaBlock.Action,
            });
        case WebsiteBlockType.ContactForm:
            const contactWidgetBlock = WebsiteBlockDisplayItem.AsContactForm(displayItem);
            return new WebsiteBlockContactFormInputItem({
                OrderIndex: contactWidgetBlock.OrderIndex,
                Header: contactWidgetBlock.Header,
                Body: contactWidgetBlock.Body,
                Images: contactWidgetBlock.Images.map(image => WebsiteBlockTextAndMediaImageDisplayItem.ConvertToInputItem(image)),
                ContactFormType: contactWidgetBlock.ContactFormType,
                ThankYouMessage: contactWidgetBlock.ThankYouMessage,
                CustomFields: contactWidgetBlock.CustomFields?.map(field => WebsiteCustomFieldContactFormDisplayItem.ConvertToInputItem(field)) ?? [],
            });
        case WebsiteBlockType.LoginForm:
            const loginWidgetBlock = WebsiteBlockDisplayItem.AsLoginForm(displayItem);
            return new WebsiteBlockLoginFormInputItem({
                OrderIndex: loginWidgetBlock.OrderIndex,
                Header: loginWidgetBlock.Header,
                Body: loginWidgetBlock.Body,
                Images: loginWidgetBlock.Images.map(image => WebsiteBlockTextAndMediaImageDisplayItem.ConvertToInputItem(image)),
                LoginFormType: loginWidgetBlock.LoginFormType,
            });
        case WebsiteBlockType.SignupForm:
            const signupWidgetBlock = WebsiteBlockDisplayItem.AsSignupForm(displayItem);
            return new WebsiteBlockSignupFormInputItem({
                Header: signupWidgetBlock.Header,
                DefaultStudentStatus: signupWidgetBlock.DefaultStudentStatus,
                AllowedStudentTypes: signupWidgetBlock.AllowedStudentTypes,
                SendLoginInformation: signupWidgetBlock.SendLoginInformation,
                SendConfirmationEmail: signupWidgetBlock.SendConfirmationEmail,
                RegistrationFeeDescription: signupWidgetBlock.RegistrationFeeDescription ?? null,
                ThankYouMessage: signupWidgetBlock.ThankYouMessage,
                TagIDs: signupWidgetBlock.TagIDs,
                SignupFormType: signupWidgetBlock.SignupFormType,
                OrderIndex: signupWidgetBlock.OrderIndex,
                EmailTemplateBody: signupWidgetBlock.EmailBodyTemplate,
                EmailTemplateSubject: signupWidgetBlock.EmailSubjectTemplate,
                PaymentType: signupWidgetBlock.PaymentType,
                RegistrationFeeAmount: signupWidgetBlock.RegistrationFeeAmount,
                RegistrationFeeChargeCategoryID: signupWidgetBlock.RegistrationFeeChargeCategoryID,
                DepositAmount: signupWidgetBlock.DepositAmount,
                BookingPriceOverride: signupWidgetBlock.BookingPriceOverride,
                HoldTimeSlot: signupWidgetBlock.HoldTimeSlot,
                TeacherIDs: signupWidgetBlock.TeacherIDs,
                EventCategoryIDs: signupWidgetBlock.EventCategoryIDs,
                AdditionalFields: !signupWidgetBlock.AdditionalFields ? null : new WebsiteBlockSignupFormAdditionalFieldsInputItem({
                    Address: signupWidgetBlock.AdditionalFields.Address,
                    Birthday: signupWidgetBlock.AdditionalFields.Birthday,
                    FaceTimeID: signupWidgetBlock.AdditionalFields.FaceTimeID,
                    Gender: signupWidgetBlock.AdditionalFields.Gender,
                    LocalSchool: signupWidgetBlock.AdditionalFields.LocalSchool,
                    PreferredTeacher: signupWidgetBlock.AdditionalFields.PreferredTeacher,
                    Referrer: signupWidgetBlock.AdditionalFields.Referrer,
                    SkillLevel: signupWidgetBlock.AdditionalFields.SkillLevel,
                    SkypeUsername: signupWidgetBlock.AdditionalFields.SkypeUsername,
                    SubjectTag: signupWidgetBlock.AdditionalFields.SubjectTag,
                    ChildEmail: signupWidgetBlock.AdditionalFields.ChildEmail,
                    ChildPhone: signupWidgetBlock.AdditionalFields.ChildPhone
                }),
                CustomFields: signupWidgetBlock.CustomFields?.map(field => WebsiteCustomFieldSignupFormDisplayItem.ConvertToInputItem(field)) ?? []
            });
        case WebsiteBlockType.Blog:
            return new WebsiteBlockBlogInputItem({
                OrderIndex: displayItem.OrderIndex,
            });
        case WebsiteBlockType.Reviews:
            const reviewsBlock = WebsiteBlockDisplayItem.AsReviews(displayItem);
            return new WebsiteBlockReviewsInputItem({
                OrderIndex: reviewsBlock.OrderIndex,
                Title: reviewsBlock.Title,
                TeacherIDs: reviewsBlock.TeacherIDs,
                ReviewsType: reviewsBlock.ReviewsType
            });
        case WebsiteBlockType.Embed:
            const embedBlock = WebsiteBlockDisplayItem.AsEmbed(displayItem);
            return new WebsiteBlockEmbedInputItem({
                OrderIndex: embedBlock.OrderIndex,
                Source: embedBlock.Source,
                IsFullWidth: embedBlock.IsFullWidth,
                ContentType: embedBlock.ContentType,
                EmbedType: embedBlock.EmbedType,
            });
    }
    AssertExhausted(displayItem.WebsiteBlockType);
}

// Convenience class to set request fields for WebsiteBlock retrieval
export class WebsiteBlockAnyDisplayItem implements WebsiteBlockDisplayItem, WebsiteBlockSignupFormDisplayItem, WebsiteBlockTextAndMediaDisplayItem, WebsiteBlockContactFormDisplayItem, WebsiteBlockEmbedDisplayItem, WebsiteBlockReviewsDisplayItem {
    DesignBlockMode?: DesignBlockMode | undefined;
    BookingPriceOverride!: BookingPriceOverrideItem | null;
    Action!: WebsiteBlockTextAndMediaActionDisplayItem;
    AdditionalFields!: WebsiteBlockSignupFormAdditionalFieldsDisplayItem | null;
    AllowedStudentTypes!: StudentTypes;
    Body!: string | null;
    CollectAddress!: boolean;
    CollectBirthday!: boolean;
    CollectCreditCard!: boolean;
    CollectFaceTimeID!: boolean;
    CollectGender!: boolean;
    CollectLocalSchool!: boolean;
    CollectPreferredTeacher!: boolean;
    CollectReferrer!: boolean;
    CollectSkillLevel!: boolean;
    CollectSkypeUsername!: boolean;
    CollectSubjectType!: boolean;
    ContactFormType!: WebsiteBlockContactFormType;
    ContentType!: WebsiteBlockEmbedContentType;
    CustomFields!: WebsiteCustomFieldSignupFormDisplayItem[];
    DefaultStudentStatus!: StudentStatus;
    DepositAmount!: number | null;
    EmailBodyTemplate!: string | null;
    EmailSubjectTemplate!: string | null;
    EmbedType!: WebsiteBlockEmbedType;
    EventCategoryIDs!: string[];
    EventPriceOverride!: number | null;
    GUID?: string | undefined;
    Header!: string | null;
    HoldTimeSlot!: boolean;
    ID!: string | null;
    Image!: WebsiteBlockTextAndMediaImageDisplayItem;
    Images!: WebsiteBlockTextAndMediaImageDisplayItem[];
    IsFullWidth!: boolean;
    Location!: string | null;
    OnlineBooking!: boolean;
    OrderIndex!: number;
    PaymentType!: SignupPaymentType | null;
    RegistrationFeeAmount!: number | null;
    RegistrationFeeChargeCategoryID!: string | null;
    RegistrationFeeDescription!: string | null;
    RegistrationFeeType!: SignupPaymentType;
    RenderContext!: WebsiteRenderContextDisplayItem | null;
    ReviewsType!: WebsiteBlockReviewsType;
    Reviews!: PublicReviewDisplayItem[];
    ReviewSummaries!: ReviewSummaryDisplayItem[];
    SchoolID!: string | null;
    ScriptTag!: string | null;
    SendConfirmationEmail!: boolean;
    SendLoginInformation!: boolean;
    SignupFormType!: WebsiteBlockSignupFormType;
    Source!: string | null;
    TagIDs!: string[];
    TeacherIDs!: string[];
    TextAndMediaType!: WebsiteBlockTextAndMediaType;
    ThankYouMessage!: string | null;
    Title!: string | null;
    TrackingCode!: string | null;
    Url!: string | null;
    WebsiteBlockType!: WebsiteBlockType;
    WebsiteID!: string | null;
    WebsitePageID!: string | null;
}
